import {observer} from "mobx-react";
import React from "react";
import {SingleValue} from "react-select";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import {FormFieldLine} from "../../../../../components/form-field-line/form-field-line";
import {Input} from "../../../../../components/inputs/input/input";
import {Toggle} from "../../../../../components/inputs/toggle/toggle";
import Card from "../../../../../components/layouts/card/card";
import ControlGroup from "../../../../../components/layouts/control-group/control-group";
import {Note} from "../../../../../components/note/note";
import config from "../../../../../config";
import DashTranslation from "../../../../../dashboard-translation/dashboard-translaton";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import {IValidationGroup} from "../../../../../hooks/validation-group/validation-group-hook";
import {FEATURES} from "../../../../../models/Plan";
import {INCENTIVE_TYPES, INCENTIVE_TYPES_MAP, INCENTIVE_VIRTUAL_CURRENCIES, INCENTIVE_VIRTUAL_CURRENCIES_MAP} from "../../../../../models/User";
import currentPortalStore from "../../../../../stores/current-portal-store";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";
import ResolveCapability from "../../../../../components/resolve-capability/resolve-capability";
import dashboardSettingsStore from "../../../../../stores/dashboard-settings-store";


interface IProps {
    onExpandClick: (expanded: boolean) => void;
    isExpanded: boolean;
    validationGroup: IValidationGroup;
    dataName?: string; /** used by Candu to target an element */
}

const RefundsPreventionsSettingsCard: React.FC<IProps> = observer(({ onExpandClick, isExpanded, validationGroup, dataName }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-settings-store';
    
    const storeSetting = currentPortalStore.setting;
    
    const isSwitchToExchangeFeatureAllowed = currentPortalStore.isFeatureAllowed(FEATURES.SWITCH_TO_EXCHANGE);
	const switchToExchangeCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.SWITCH_TO_EXCHANGE);
    
    const getSwitchToExchangeNoteText = () => {
        const isProductExchangeFeatureAllowed = currentPortalStore.isFeatureAllowed(FEATURES.PRODUCT_EXCHANGE);
        const isProductExchangeInExchangeTypes = storeSetting?.switchToExchangeStore.eligibleReturnReasons?.some(returnReason => returnReason.exchange_types?.includes('product exchange'));
        const isVariantExchangeInExchangeTypes = storeSetting?.switchToExchangeStore.eligibleReturnReasons?.some(returnReason => returnReason.exchange_types?.includes('variant exchange'));
        if (!storeSetting?.switchToExchangeStore.eligibleReturnReasons.length) {
            return 'SWITCH_TO_EXCHANGE_NO_RETURN_REASONS';
        } else if (!isVariantExchangeInExchangeTypes && isProductExchangeInExchangeTypes && isProductExchangeFeatureAllowed) {
            return 'SWITCH_TO_EXCHANGE_NOTE_PRODUCT_EXCHANGE';
        } else if (!isProductExchangeFeatureAllowed || !isProductExchangeInExchangeTypes) {
            return 'SWITCH_TO_EXCHANGE_NOTE_VARIANT_EXCHANGE';
        }
        
        return 'SWITCH_TO_EXCHANGE_NOTE_BOTH';
    }
    
    const onChangeIsSwitchToExchangeActive = (value: boolean) => {
        storeSetting?.switchToExchangeStore.setIsActive(value);
    }
    
    const incentiveVirtualCurrencyOptions = [
        {
            label: getTranslation(INCENTIVE_VIRTUAL_CURRENCIES_MAP[INCENTIVE_VIRTUAL_CURRENCIES.GIFT_CARD].label, pageScope),
            value: INCENTIVE_VIRTUAL_CURRENCIES.GIFT_CARD,
	        disabled: !switchToExchangeCapability.isGiftCardIncentiveSupported
        },
        {
            label: getTranslation(INCENTIVE_VIRTUAL_CURRENCIES_MAP[INCENTIVE_VIRTUAL_CURRENCIES.STORE_CREDITS].label, pageScope),
            value: INCENTIVE_VIRTUAL_CURRENCIES.STORE_CREDITS,
	        disabled: !switchToExchangeCapability.isStoreCreditsIncentiveSupported
        },
    ];
    
    const incentiveVirtualCurrency = storeSetting?.switchToExchangeStore.incentiveVirtualCurrency ? {
        value: storeSetting.switchToExchangeStore.incentiveVirtualCurrency,
        label: getTranslation(INCENTIVE_VIRTUAL_CURRENCIES_MAP[storeSetting.switchToExchangeStore.incentiveVirtualCurrency].label, pageScope)
    } : undefined;
    const onChangeIncentiveVirtualCurrency = (value: SingleValue<IOption>) => {
        storeSetting?.switchToExchangeStore.setIncentiveVirtualCurrency(value?.value as INCENTIVE_VIRTUAL_CURRENCIES);
    }
    
    const incentiveTypeOptions = [
        {
            label: getTranslation(INCENTIVE_TYPES_MAP[INCENTIVE_TYPES.FIXED_PRICE].label, pageScope),
            value: INCENTIVE_TYPES.FIXED_PRICE
        },
        {
            label: getTranslation(INCENTIVE_TYPES_MAP[INCENTIVE_TYPES.PERCENTAGE].label, pageScope),
            value: INCENTIVE_TYPES.PERCENTAGE
        },
    ];
    
    const incentiveType = storeSetting?.switchToExchangeStore.incentiveType ? {
        value: storeSetting.switchToExchangeStore.incentiveType,
        label: getTranslation(INCENTIVE_TYPES_MAP[storeSetting.switchToExchangeStore.incentiveType].label, pageScope)
    } : undefined;
    
    const onChangeIncentiveType = (value: SingleValue<IOption>) => {
        storeSetting?.switchToExchangeStore.setIncentiveType(value?.value as INCENTIVE_TYPES);
    }
    
    const onChangeIncentiveAmount = (value: number) => {
        storeSetting?.switchToExchangeStore.setIncentiveAmount(+value.toFixed(0));
    }
    
    return (
	    <ResolveCapability capability={PROFILE_CAPABILITIES.SWITCH_TO_EXCHANGE}>
		    {({ isSupported, isFeatureEnabled }) => isSupported && isFeatureEnabled && (
			    <Card
				    toolbar={{
					    title: (
						    <FontBody1 variant={FONT_VARIANT.BOLD}>
							    <DashTranslation translationKey="REFUNDS_PREVENTION" scope={pageScope} />
						    </FontBody1>
					    )
				    }}
				    expandCallback={onExpandClick}
				    isExpanded={isExpanded}
				    dataName={dataName}
			    >

				    <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
					    {getTranslation('SWITCH_TO_EXCHANGE', pageScope)}
				    </FontBody2>

				    <FormFieldLine
					    tooltip={!isSwitchToExchangeFeatureAllowed ? (
						    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
							    <DashTranslation translationKey="SWITCH_REFUND_REQUEST_TO_EXCHANGE_TOOLTIP" scope={pageScope} />
						    </FontBody2>
					    ) : null
					    }
					    label={getTranslation('SWITCH_REFUND_REQUEST_TO_EXCHANGE', pageScope)}
					    dataName="switch-refund-request-to-exchange"
				    >
					    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
						    <Toggle
							    checked={!!storeSetting?.switchToExchangeStore.isActive}
							    onChange={onChangeIsSwitchToExchangeActive}
							    disabled={!isSwitchToExchangeFeatureAllowed}
							    label=''
						    />
						    {!!storeSetting?.switchToExchangeStore.isActive && (
							    <div>
								    {(
									    <FontBody2 color={FONT_COLOR.PRIMARY3}>
										    Active in {storeSetting?.switchToExchangeStore.eligibleReturnReasons.length} <a href={config.apiUrl+"/dashboard/settings/return-reasons"} target='_parent'><FontBody2 color={FONT_COLOR.PRIMARY3}>{storeSetting?.switchToExchangeStore.eligibleReturnReasons.length !== 1 ? 'return reasons' : 'return reason'}</FontBody2></a>
									    </FontBody2>
								    ) }
							    </div>
						    )}
					    </div>
				    </FormFieldLine>
				    { storeSetting?.switchToExchangeStore.eligibleReturnReasons.length === 0 &&
					    !!storeSetting?.switchToExchangeStore.isActive &&
					    isSwitchToExchangeFeatureAllowed &&
					    <Note body={'Switch to Exchange is offered based on return reasons. After configuring the bonus, make sure to define which return reasons are eligible.'}/>

				    }
				    {!!storeSetting?.switchToExchangeStore.isActive &&
					    <FontBody2 variant={FONT_VARIANT.DEFAULT} color={FONT_COLOR.NEUTRAL3}>
						    <DashTranslation translationKey="SWITCH_TO_EXCHANGE_INSTRUCTIONS" scope={pageScope}/>
					    </FontBody2>
				    }
				    {!!storeSetting?.switchToExchangeStore.isActive && (
					    <>
						    <FormFieldLine
							    label={getTranslation('VIRTUAL_CURRENCY', pageScope)}
							    note={getTranslation('VIRTUAL_CURRENCY_NOTE', pageScope)}
							    tooltip={
								    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
									    <DashTranslation translationKey={getSwitchToExchangeNoteText()} scope={pageScope} />
								    </FontBody2>
							    }
							    dataName="virtual-currency"
						    >
							    <SelectDropdown
								    options={incentiveVirtualCurrencyOptions}
								    value={incentiveVirtualCurrency}
								    onChange={onChangeIncentiveVirtualCurrency}
								    placeholder='Please select'
								    required
								    validationGroup={validationGroup}
							    />
						    </FormFieldLine>
						    <FormFieldLine
							    label={getTranslation('SELECT_INCENTIVE_TYPE', pageScope)}
							    dataName="select-incentive-type"
						    >
							    <ControlGroup>
								    <SelectDropdown
									    options={incentiveTypeOptions}
									    value={incentiveType}
									    onChange={onChangeIncentiveType}
									    required
									    validationGroup={validationGroup}
								    />
								    <div style={{display: 'flex', alignItems: 'baseline', gap: '10px'}}>
									    <Input
										    value={storeSetting?.switchToExchangeStore.incentiveAmount}
										    onChange={onChangeIncentiveAmount}
										    type="number"
										    min={1}
										    step={1}
										    required
										    validationGroup={validationGroup}
									    />
									    <FontBody2>{storeSetting?.switchToExchangeStore.incentiveType === INCENTIVE_TYPES.FIXED_PRICE ? storeSetting?.switchToExchangeStore.incentiveCurrency : '%'}</FontBody2>
								    </div>
								    {!!storeSetting?.switchToExchangeStore.incentiveAmount && (
									    <FontBody2 color={FONT_COLOR.NEUTRAL3}>
										    {storeSetting?.switchToExchangeStore.incentiveType === INCENTIVE_TYPES.FIXED_PRICE ? (
											    <>
												    Fixed = item price + <FontBody2 color={FONT_COLOR.PRIMARY1}>{storeSetting?.switchToExchangeStore.incentiveAmount} {storeSetting?.switchToExchangeStore.incentiveCurrency}</FontBody2>
											    </>
										    ) : (
											    <>
												    Percentage = item price + <FontBody2 color={FONT_COLOR.PRIMARY1}>{storeSetting?.switchToExchangeStore.incentiveAmount}%</FontBody2>
											    </>
										    )}
									    </FontBody2>
								    )}
							    </ControlGroup>
						    </FormFieldLine>
					    </>
				    )}
			    </Card>
		    )}
		</ResolveCapability>
    );
});

export default RefundsPreventionsSettingsCard;
